import { useState } from "react";

import { Button, Callout } from "@blueprintjs/core";
import { useParams, useSearchParams } from "react-router";

import joinSessionSvg from "@/../../hypetrain/static/img/join-session.svg";
import serviceCompleteSvg from "@/../../hypetrain/static/img/service-complete.svg";
import sessionCanceledSvg from "@/../../hypetrain/static/img/session-canceled.svg";
import { isPendingBookingRequest } from "@/components/helpers/meeting/is-pending-booking-request";
import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import {
  isAttendees,
  isCoach,
} from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { Nav } from "@/components/nav/Nav";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { MeetingSkeleton } from "@/components/pages_logged_in/session/MeetingSkeleton";
import { PermissionErrorPage } from "@/components/pages_public/403";
import { FlexibleButton } from "@/components/pieces/button/FlexibleButton";
import { BookingRequestPending } from "@/components/pieces/coaching/meetings/BookingRequestPending";
import { MeetingHeader } from "@/components/pieces/coaching/meetings/MeetingHeader";
import { MeetingSidebar } from "@/components/pieces/coaching/meetings/MeetingSidebar";
import { getMeetingStatus } from "@/components/pieces/coaching/meetings/utils";
import { ServiceChat } from "@/components/pieces/coaching/ServiceChat";
import { LinkButton } from "@/components/pieces/link/LinkButton";
import ComponentInfo from "@/components/pieces/products/ComponentInfo";
import { ProductDetails } from "@/components/pieces/products/ProductDetails";
import { SubmittedInformation } from "@/components/pieces/products/SubmittedInformation";
import { getUser } from "@/components/session/JwtTokenManager";
import {
  CARD_CLASSES,
  SIDE_NAV_WIDTH,
  SITE_NAME,
  STANDARD_APP_MAX_WIDTH,
  SUBTEXT_COLOR,
} from "@/css/constants";
import {
  PaymentStatusTypes,
  useBookingRequestTimeslotsQuery,
  useFullMeetingQuery,
} from "@/graphql";
import { FullMeeting } from "@/types/meetings";
import { RoleView } from "@/types/users";
import { ensureNonNullArray } from "@/utils/array";

interface SessionCardProps {
  icon: string;
  title: React.ReactNode;
  subtitle: React.ReactNode;
  actionButton?: React.ReactNode | null;
  iconWidth?: number;
  children?: React.ReactNode | null;
}

function SessionCard({
  icon,
  title,
  subtitle,
  actionButton = null,
  iconWidth = 64,
  children = null,
}: SessionCardProps) {
  return (
    <Flex
      mb={24}
      p={24}
      backgroundColor="#FFF"
      className={CARD_CLASSES}
      justifyContent="space-between"
      flexWrap="wrap"
      alignItems="center"
    >
      <Flex alignItems="center">
        <EmbedSvg src={icon} width={iconWidth} />
        <Flex
          ml={3}
          flex={1}
          flexDirection={["column", "column", "column", "row"]}
          flexWrap={"wrap"}
        >
          <Box flex={1} minWidth={250} mr={2}>
            <Box fontWeight={500} fontSize={20}>
              {title}
            </Box>
            <Box color={SUBTEXT_COLOR}>{subtitle}</Box>
          </Box>

          {actionButton && (
            <Box width={["100%", "100%", "100%", "auto"]} minWidth={180} mt={2}>
              {actionButton}
            </Box>
          )}
        </Flex>
      </Flex>
      {children}
    </Flex>
  );
}

type SessionViewType =
  | "JOIN_NOW"
  | "SESSION_CANCELED"
  | "SESSION_COMPLETE"
  | "INTRO_GUIDE";

interface SessionViewProps {
  type: SessionViewType;
  meeting: FullMeeting;
  isCoach: boolean;
}

function SessionView({ type, meeting, isCoach }: SessionViewProps) {
  const scheduleSessionButton = (
    <FlexibleButton
      as={LinkButton}
      to={`/schedule/${meeting.coach?.id}`}
      style={{
        padding: "12px 24px",
      }}
    >
      Schedule Session
    </FlexibleButton>
  );

  switch (type) {
    case "JOIN_NOW":
      return (
        <SessionCard
          icon={joinSessionSvg}
          iconWidth={74}
          title="Your session is happening now"
          subtitle="Start where you are. Use what you have. Do what you can."
          actionButton={
            <Button
              intent="primary"
              large={true}
              text="Join Session"
              onClick={() => {
                if (meeting.location) {
                  window.open(meeting.location, "_blank");
                }
              }}
            />
          }
        />
      );

    case "SESSION_CANCELED":
      return (
        <SessionCard
          icon={sessionCanceledSvg}
          title="This session is canceled"
          subtitle="Every day is a new beginning. Take a deep breath and start again."
          actionButton={isCoach ? null : scheduleSessionButton}
        />
      );

    case "SESSION_COMPLETE":
      return (
        <SessionCard
          icon={serviceCompleteSvg}
          title="All done!"
          subtitle="The journey of a thousand miles begins with one step"
          actionButton={isCoach ? null : scheduleSessionButton}
        />
      );

    case "INTRO_GUIDE":
      return (
        <SessionCard
          icon={joinSessionSvg}
          title="Turn this intro session into an ongoing relationship"
          subtitle="Read our guide on how to schedule follow-up sessions"
          actionButton={
            <LinkButton
              to="/learn/follow-ups"
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "12px 24px",
              }}
            >
              Read The Guide
            </LinkButton>
          }
        />
      );

    default:
      return null;
  }
}

interface AuthSessionPageProps {
  userId?: string;
  view?: RoleView;
}

function AuthSessionPage({ userId, view = "coach" }: AuthSessionPageProps) {
  const { meetingId } = useParams();
  const [errorText, setErrorText] = useState<string>();
  const [searchParams] = useSearchParams();
  const showIntroMessage = searchParams.get("showIntroMessage");

  const { data } = useFullMeetingQuery({
    variables: {
      id: meetingId || "",
      customerId: userId,
    },
    onError: () => {
      setErrorText(
        "An issue has arise when trying to getting this meeting. Please contact us",
      );
    },
    skip: !meetingId,
  });

  const { data: timeslotData, loading: loadingTimeslots } =
    useBookingRequestTimeslotsQuery({
      variables: {
        meetingId: meetingId || "",
        isConflictCheck: true,
      },
      skip: !isCoach(),
    });

  if (!data) {
    return (
      <>
        <Nav />
        <Box ml={[0, 0, SIDE_NAV_WIDTH]}>
          {errorText && (
            <Box textAlign={"center"}>
              <Callout intent="danger" icon={null}>
                {errorText}
              </Callout>
            </Box>
          )}
          <MeetingSkeleton />
        </Box>
      </>
    );
  }

  const { meeting, currentUser } = data;
  const { programComponent, paymentStatus, coach, product } = meeting || {};
  const user = getUser();

  if (!meeting || !product || !user) {
    return <PermissionErrorPage />;
  }

  const meetingStatus = getMeetingStatus(meeting);
  const isCoachOfMeeting = user.id === meeting.coach?.id;
  const isIntroProduct = product.introProduct;

  // Determine which view to show
  const getSessionViewType = () => {
    if (meetingStatus === "SOON" && meeting?.location) {
      return "JOIN_NOW";
    } else if (meeting?.canceledAt) {
      return "SESSION_CANCELED";
    } else if (meetingStatus === "COMPLETE") {
      return "SESSION_COMPLETE";
    }

    return null;
  };

  const sessionViewType = getSessionViewType();

  const answers = ensureNonNullArray(meeting.formFieldAnswers).filter(
    (a) => a.formField?.answerType !== "BOOK_MEETING",
  );

  return (
    <>
      <SEOHead
        title={`${product?.name} - ${SITE_NAME}`}
        description={`See details of your ${SITE_NAME} session`}
        canonicalPath={`/sessions/${meeting.id}`}
        image="https://placement-pub.s3.us-east-2.amazonaws.com/sessions-meta.png"
      />
      <Nav />
      <Box ml={[0, 0, SIDE_NAV_WIDTH]}>
        <MeetingHeader
          meeting={meeting}
          loading={false}
          view={view}
          customerId={user?.id}
        />
        <Flex
          maxWidth={STANDARD_APP_MAX_WIDTH}
          m="0 auto"
          px={[3, 24]}
          pb={6}
          flexDirection={["column", "column", "column", "row"]}
        >
          <Box mt={4} flex={1}>
            {isPendingBookingRequest(meeting) && (
              <BookingRequestPending
                isCoach={isCoachOfMeeting}
                meeting={meeting}
                timeslotData={timeslotData}
                loadingTimeslot={loadingTimeslots}
              />
            )}

            {/* Intro session welcome message */}
            {showIntroMessage && data?.meeting?.product?.introProduct && (
              <Box mb={4}>
                <Callout intent="primary" icon={null}>
                  <Flex alignItems={"center"}>
                    <Box>
                      <Box fontWeight={500} mb={1}>
                        Welcome to your intro session!
                      </Box>
                      This is a great opportunity to get to know each other and
                      discuss your goals. Once you've completed the session,
                      you'll be able to schedule follow-up sessions.
                    </Box>
                  </Flex>
                </Callout>
              </Box>
            )}

            {paymentStatus === PaymentStatusTypes.Unpaid && (
              <Box mb={4}>
                <Callout intent="warning" icon={null}>
                  <Flex alignItems={"center"}>
                    <Box>
                      Finish checkout to confirm your session with{" "}
                      {coach?.name || "the coach"}
                    </Box>
                  </Flex>
                </Callout>
              </Box>
            )}

            {/* Render the appropriate session view */}
            {sessionViewType && (
              <SessionView
                type={sessionViewType}
                meeting={meeting}
                isCoach={isCoachOfMeeting}
              />
            )}

            {programComponent && !isCoachOfMeeting && (
              <ComponentInfo programComponent={programComponent} />
            )}

            {/* Render intro guide if needed */}
            {isIntroProduct && isCoachOfMeeting && (
              <SessionView
                type="INTRO_GUIDE"
                meeting={meeting}
                isCoach={isCoachOfMeeting}
              />
            )}

            <MeetingSidebar meeting={meeting} currentUser={currentUser} />

            <SubmittedInformation answers={answers} />
            <Box
              mt={4}
              backgroundColor="#FFF"
              className={CARD_CLASSES}
              p={[3, 3, 24]}
              mb={[24, 24, 24, 0]}
            >
              <Box fontSize={18} fontWeight={500}>
                About This Service
              </Box>
              <ProductDetails
                product={product}
                coachPrice={meeting.creditPrice}
                hideCost={isAttendees(meeting)}
              />
            </Box>
          </Box>
          <Box mt={4} width={["100%", "100%", "100%", 400]} ml={[0, 0, 0, 3]}>
            <ServiceChat
              isCoach={isCoachOfMeeting}
              customer={meeting.customer}
              coach={meeting.coach}
              chatChannelId={meeting.chatChannelId ?? ""}
            />
          </Box>
        </Flex>
      </Box>
    </>
  );
}

export function SessionPage() {
  return (
    <>
      <Authenticate maxWidth="100%" layout="dashboard">
        <AuthSessionPage />
      </Authenticate>
    </>
  );
}
